import React, { useEffect, useMemo, useState } from "react";
import api, { API, formatIDR } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, Search, Wallet, Download } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { date: new Date().toISOString().slice(0, 10), category: "", amount: 0, note: "", order_id: "" };

const Expenses = () => {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    const params = {};
    if (filterCat) params.category = filterCat;
    if (start) params.start = start;
    if (end) params.end = end;
    const { data } = await api.get("/expenses", { params });
    setList(data);
  };
  const loadRefs = async () => {
    const [c, o] = await Promise.all([api.get("/expenses/categories"), api.get("/orders")]);
    setCats(c.data.categories || []);
    setOrders(o.data || []);
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, start, end]);

  const filtered = useMemo(() => {
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter((e) => (e.category || "").toLowerCase().includes(s) || (e.note || "").toLowerCase().includes(s));
  }, [search, list]);

  const total = useMemo(() => filtered.reduce((s, e) => s + Number(e.amount || 0), 0), [filtered]);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setModal(true); };
  const openEdit = (e) => {
    setForm({ date: e.date, category: e.category, amount: e.amount, note: e.note || "", order_id: e.order_id || "" });
    setEditingId(e.id); setModal(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const payload = { ...form, amount: Number(form.amount) };
    try {
      if (editingId) await api.put(`/expenses/${editingId}`, payload);
      else await api.post("/expenses", payload);
      toast.success("Pengeluaran tersimpan");
      setModal(false); load(); loadRefs();
    } catch { toast.error("Gagal menyimpan"); }
  };

  const remove = async () => {
    await api.delete(`/expenses/${confirmDel.id}`);
    setConfirmDel(null); toast.success("Pengeluaran dihapus"); load();
  };

  const orderLabel = (id) => {
    const o = orders.find((x) => x.id === id);
    return o ? `${o.order_no} · ${o.customer?.name || ""}` : "—";
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-cap mb-2">Cash Out</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Pengeluaran</h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <button onClick={openCreate} data-testid="add-expense-button" className="bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
            <Plus size={14} /> Tambah Pengeluaran
          </button>
          <button
            onClick={() => {
              const token = localStorage.getItem("om_token");
              const params = new URLSearchParams({ start, end, category: filterCat, token: token || "" });
              window.open(`${API}/reports/expenses.xlsx?${params.toString()}`, "_blank");
            }}
            data-testid="expenses-download-xlsx"
            className="bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2"
            title="Download Excel: Sheet 1 rangkuman per kategori, Sheet 2 detail keseluruhan"
          >
            <Download size={14} /> Download Excel
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] p-4 mb-4 flex items-end gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] px-2">
          <Search size={14} className="text-[#52525B]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="expense-search-input" placeholder="Cari kategori/catatan…" className="flex-1 outline-none text-sm bg-transparent" />
        </div>
        <div>
          <label className="label-cap block mb-1">Kategori</label>
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="expense-cat-filter" className="px-2 py-1.5 border border-[#E4E4E7] text-xs bg-white">
            <option value="">Semua</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="label-cap block mb-1">Dari</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="px-2 py-1.5 border border-[#E4E4E7] text-xs font-mono bg-white" data-testid="expense-start-input" />
        </div>
        <div>
          <label className="label-cap block mb-1">Sampai</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="px-2 py-1.5 border border-[#E4E4E7] text-xs font-mono bg-white" data-testid="expense-end-input" />
        </div>
        <div className="ml-auto bg-[#111113] text-white px-4 py-2 flex items-center gap-2">
          <Wallet size={14} />
          <span className="label-cap text-white/60">Total</span>
          <span className="font-mono font-bold text-base" data-testid="expense-total">{formatIDR(total)}</span>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#E4E4E7]">
              <th className="text-left label-cap px-4 py-3">Tanggal</th>
              <th className="text-left label-cap px-4 py-3">Kategori</th>
              <th className="text-left label-cap px-4 py-3">Catatan</th>
              <th className="text-left label-cap px-4 py-3">Order Terkait</th>
              <th className="text-right label-cap px-4 py-3">Jumlah</th>
              <th className="text-right label-cap px-4 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-sm text-[#52525B]">Belum ada pengeluaran.</td></tr>}
            {filtered.map((e) => (
              <tr key={e.id} className="border-b border-[#E4E4E7] row-hover" data-testid={`expense-row-${e.id}`}>
                <td className="px-4 py-3 text-sm font-mono">{e.date}</td>
                <td className="px-4 py-3 text-sm font-bold">
                  {e.category}
                  {e.auto_source === "payroll" && (
                    <span className="ml-2 inline-block text-[9px] font-bold uppercase tracking-wider bg-[#7C3AED]/10 text-[#7C3AED] border border-[#7C3AED]/30 px-1.5 py-0.5" data-testid={`expense-auto-payroll-${e.id}`}>Auto · Payroll</span>
                  )}
                  {e.auto_source === "petty_cash" && (
                    <span className="ml-2 inline-block text-[9px] font-bold uppercase tracking-wider bg-[#059669]/10 text-[#059669] border border-[#059669]/30 px-1.5 py-0.5">Petty Cash</span>
                  )}
                  {e.auto_source === "travel_fund" && (
                    <span className="ml-2 inline-block text-[9px] font-bold uppercase tracking-wider bg-[#D97706]/10 text-[#D97706] border border-[#D97706]/30 px-1.5 py-0.5">Uang Jalan</span>
                  )}
                  {e.auto_source === "petty_cash_medan" && (
                    <span className="ml-2 inline-block text-[9px] font-bold uppercase tracking-wider bg-[#0891B2]/10 text-[#0891B2] border border-[#0891B2]/30 px-1.5 py-0.5">Petty Cash Medan</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-[#52525B] max-w-xs truncate">{e.note}</td>
                <td className="px-4 py-3 text-xs font-mono">{e.order_id ? orderLabel(e.order_id) : "—"}</td>
                <td className="px-4 py-3 text-sm font-mono font-bold text-right">{formatIDR(e.amount)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {e.auto_source === "payroll" ? (
                      <span className="text-[10px] text-[#52525B] italic pr-1">Kelola di Payroll</span>
                    ) : e.auto_source === "petty_cash" ? (
                      <span className="text-[10px] text-[#52525B] italic pr-1">Kelola di Petty Cash</span>
                    ) : e.auto_source === "travel_fund" ? (
                      <span className="text-[10px] text-[#52525B] italic pr-1">Kelola di Uang Jalan</span>
                    ) : e.auto_source === "petty_cash_medan" ? (
                      <span className="text-[10px] text-[#52525B] italic pr-1">Kelola di Petty Cash Medan</span>
                    ) : (
                      <>
                        <button onClick={() => openEdit(e)} data-testid={`expense-edit-${e.id}`} className="p-2 hover:bg-[#F4F4F5] border border-[#E4E4E7]"><Pencil size={14} /></button>
                        <button onClick={() => setConfirmDel(e)} data-testid={`expense-delete-${e.id}`} className="p-2 hover:bg-[#DC2626]/10 text-[#DC2626] border border-[#E4E4E7]"><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-[#E4E4E7] w-full max-w-xl" data-testid="expense-form">
            <div className="px-6 py-4 border-b border-[#E4E4E7]">
              <div className="label-cap">{editingId ? "Ubah" : "Tambah"} Pengeluaran</div>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <F label="Tanggal" required>
                <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={ic + " font-mono"} data-testid="expense-date-input" />
              </F>
              <F label="Kategori" required>
                <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={ic} data-testid="expense-category-input">
                  <option value="">— Pilih kategori —</option>
                  {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <p className="text-[10px] text-[#52525B] mt-1">Kategori dikelola di Pengaturan.</p>
              </F>
              <F label="Jumlah (IDR)" required>
                <input type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={ic + " font-mono"} data-testid="expense-amount-input" />
              </F>
              <F label="Order Terkait (opsional)">
                <select value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} className={ic} data-testid="expense-order-select">
                  <option value="">— Tidak terkait order —</option>
                  {orders.map((o) => <option key={o.id} value={o.id}>{o.order_no} · {o.customer?.name}</option>)}
                </select>
              </F>
              <F label="Catatan" colSpan>
                <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={ic} data-testid="expense-note-input" />
              </F>
            </div>
            <div className="px-6 py-4 border-t border-[#E4E4E7] flex justify-end gap-2">
              <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button type="submit" data-testid="expense-submit-button" className="px-4 py-2 text-xs font-bold uppercase bg-[#002FA7] text-white hover:bg-[#00227A]">Simpan</button>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setConfirmDel(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#E4E4E7] max-w-sm w-full p-6">
            <h3 className="font-heading text-lg font-bold mb-2">Hapus Pengeluaran?</h3>
            <p className="text-sm text-[#52525B] mb-6">{confirmDel.category} · {formatIDR(confirmDel.amount)}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-xs font-bold uppercase border border-[#E4E4E7]">Batal</button>
              <button onClick={remove} data-testid="confirm-delete-expense" className="px-4 py-2 text-xs font-bold uppercase bg-[#DC2626] text-white">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const ic = "w-full px-3 py-2 border border-[#E4E4E7] text-sm outline-none focus:border-[#002FA7] bg-white";
const F = ({ label, children, required, colSpan }) => (
  <div className={colSpan ? "col-span-2" : ""}>
    <label className="label-cap block mb-1.5">{label}{required && <span className="text-[#DC2626]">*</span>}</label>
    {children}
  </div>
);

export default Expenses;
